import { useEffect, useState } from 'react';
import type { IAsset } from '../../../../interfaces/asset';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import type { IDermaCV1RequestCustomControlData } from './DermaCV1RequestAddEdit';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { getOrderedAssets } from '../../../../../../common/utils/photoAssetUtils';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider, HeaderDivider, ShowYesNo, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { AssetThumbnailsWithHeader, ProcessContainer, ProfileImageWithName } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { DermaCV1FollowingSteps } from './DermaCV1FollowingSteps';
import { DermaCV1RequestSummary } from './DermaCV1RequestSummary';

export function DermaCV1RequestRead(props: ICustomControlRequestReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);
    const [hasMedications, setHasMedications] = useState(false);
    const [orderedAssets, setOrderedAssets] = useState<IAsset[]>([]);
    const customControlData: IDermaCV1RequestCustomControlData = props.request.customControlData;

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.request, hasMedications]);

    useEffect(() => {
        setOrderedAssets(getOrderedAssets(props.request));
    }, [props.request]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={props.process.serviceType}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            {!props.itemState.isOpen && (
                <DermaCV1RequestSummary
                    {...props}
                    orderedAssets={orderedAssets}
                />
            )}

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.request.status !== RequestStatus.requested ? (
                    <HorizontalFormGroupRead>
                        <FormLabel>{translateKey('request.sendTo')}</FormLabel>
                        <InputContainer>
                            <ProfileImageWithName
                                organization={props.request.recipientOrganization}
                                practitioner={props.request.recipientPractitioner}
                            />
                        </InputContainer>
                    </HorizontalFormGroupRead>
                ) : (
                    <ProfileImages request={props.request} />
                )}

                <AssetThumbnailsWithHeader
                    assets={orderedAssets}
                    id={props.request.id}
                    withFormDivider
                />

                {isAnamneseVisible() && (
                    <>
                        <FormDivider />
                        <HeaderDivider text={translateKey('carePlan.anamnese')} />
                    </>
                )}

                {isSymptomenVisible() && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Symptomen</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{showSymptomen()}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData?.beschrijving_klachten && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Beschrijving klachten</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.beschrijving_klachten}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData?.duur_klachten && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Duur klachten</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.duur_klachten}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.recidief !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Recidief</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                <ShowYesNo value={customControlData.recidief} />
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.chronisch !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Chronisch</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                <ShowYesNo value={customControlData.chronisch} />
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData?.lichaamsdeel && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Lichaamsdeel</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.lichaamsdeel}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.behandeling_geprobeerd !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Behandeling geprobeerd</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                <ShowYesNo value={customControlData.behandeling_geprobeerd} />
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.behandeling_geprobeerd_remark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel></FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.behandeling_geprobeerd_remark}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.familiaire_huidziekten !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Familiaire huidziekten</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                <ShowYesNo value={customControlData.familiaire_huidziekten} />
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.familiaire_huidziekten_remark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel></FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.familiaire_huidziekten_remark}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.bewezen_allergie !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bewezen allergie</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                <ShowYesNo value={customControlData.bewezen_allergie} />
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData && customControlData.bewezen_allergie_remark !== null && (
                    <HorizontalFormGroupRead>
                        <FormLabel></FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.bewezen_allergie_remark}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {(hasMedications || isVraagVisible()) && <FormDivider />}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    onContentLoaded={async (hasContent) => setHasMedications(hasContent)}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList
                    onContentLoaded={async (hasContent) => setHasMedications(hasContent)}
                    requestId={props.request.id}
                />

                {isVraagVisible() && <HeaderDivider text={translateKey('request.question')} />}

                <Icd10SelectionsRead
                    diagnosis={props.request.diagnosis}
                    diagnosisConclusion={props.request.customControlData?.conclusie_diagnose}
                    hideTitle
                    showWorkingDiagnosis
                    thesaurusTermDate={props.request.dateSent}
                />

                {customControlData?.vraag && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Vraag</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.vraag}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData?.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <DermaCV1FollowingSteps />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('request.hideConsult')}
                    labelMore={translateKey('request.showConsult')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <RequestReadButtons
                {...props}
                editRequest={handleEditRequest}
            />
        </ProcessContainer>
    );

    async function handleEditRequest() {
        // Reload process to retrieve possibly uploaded images via the app after this process was saved as draft.
        await props.reloadRequest();
        await props.editRequest();
    }

    function checkIsFooterVisible() {
        if (
            (props.request.assets && props.request.assets.length > 0) ||
            isAnamneseVisible() ||
            isVraagVisible() ||
            props.request.recipientOrganizationId ||
            props.request.recipientPractitionerId ||
            hasMedications
        ) {
            setIsFooterVisible(true);
        }
    }

    function isSymptomenVisible() {
        return (
            customControlData &&
            (customControlData.symptomen.jeuk ||
                customControlData.symptomen.pijn ||
                customControlData.symptomen.bloeden ||
                customControlData.symptomen.cosmetisch ||
                customControlData.symptomen.anders)
        );
    }

    function showSymptomen() {
        return (
            customControlData &&
            [
                customControlData.symptomen.jeuk ? 'Jeuk' : '',
                customControlData.symptomen.pijn ? 'Pijn' : '',
                customControlData.symptomen.bloeden ? 'Bloeden' : '',
                customControlData.symptomen.cosmetisch ? 'Cosmetisch' : '',
                customControlData.symptomen.anders,
            ]
                .filter((item) => item)
                .join(', ')
        );
    }

    function isAnamneseVisible() {
        return (
            isSymptomenVisible() ||
            (customControlData &&
                (customControlData.beschrijving_klachten ||
                    customControlData.duur_klachten ||
                    customControlData.lichaamsdeel ||
                    customControlData.chronisch !== null ||
                    customControlData.recidief !== null ||
                    customControlData.behandeling_geprobeerd !== null ||
                    customControlData.familiaire_huidziekten !== null ||
                    customControlData.bewezen_allergie !== null))
        );
    }

    function isVraagVisible() {
        return (
            customControlData &&
            (customControlData.toelichting ||
                customControlData.conclusie_diagnose ||
                props.request.diagnosis?.diagnosisConsiderationIcd10)
        );
    }
}
